
/*
 * VNCtalk - an enterprise real-time communication solution including chat, video and audio conferencing, screen sharing, voice messaging, file sharing, broadcasts, document collaboration and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Observable, Observer } from "rxjs";
import { CommonUtil } from "../talk/utils/common.util";

export class Utils {
  private static fileExtensionMap = {
    Image: ["png", "jpg", "jpeg", "gif", "bmp"],
    Pdf: ["pdf"],
    Excel: ["xls", "xlsx", "xlsm", "xltx", "xltm", "ods", "csv"],
    Word: ["doc", "docx", "dot", "dotm", "dot", "dotx", "docm", "docb", "wbk", "odf"],
    Powerpoint: ["ppt", "pptx", "pptm", "odp"],
    Sound: ["mp3", "aac", "wma", "wav"],
    Video: ["mp4", "webm", "vob", "ogg", "gif", "avi", "mkv", "flv", "mov", "wmv", "m4v", "3gp"],
    Text: ["txt", "odt"],
    Archive: ["iso", "zip", "7z", "rar", "tar", "gz"]
  };

  static getFileType(file: {name: string}) {
    let fileExt = this.getExtensionType(file.name);
    for (let type in this.fileExtensionMap) {
      if (this.fileExtensionMap[type].filter(ext => ext === fileExt.toLowerCase()).length)
        return type;
    }
    return "File";
  }

  static VALIDATORS = {
    // copied from: https://github.com/yuyang041060120/ng2-validation/blob/master/src/url/validator.ts
    URL: /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i,
    EMAIL: /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/
  };

  static KEYCODES = {
    BACKSPACE: 8,
    ARROW_UP: 40,
    ARROW_DOWN: 38,
    ARROW_LEFT: 37,
    ARROW_RIGHT: 39,
    ENTER: 13
  };

  static isImage(mimeType: string) {
    let validImageMimeTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/gif",
      "image/bmp",
      "image/x-icon"
    ];
    return validImageMimeTypes.indexOf(mimeType) >= 0;
  }

  static isConvertableToPDF(mimeType: string) {
    let validImageMimeTypes = [
      "application/pdf"
    ];
    return validImageMimeTypes.indexOf(mimeType) >= 0;
  }

  static saveFile = (function () {
  let a = document.createElement("a");
  document.body.appendChild(a);
  a["style"]["display"] = "none";
  return function (blob, fileName) {
      let url = window.URL.createObjectURL(blob);
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    };
  }());

  /* Helper function */
  static downloadFileViaUrl(fileURL) {
    const file_path = fileURL;
    const a: any = document.createElement("A");
    a.href = CommonUtil.getAttachmentLocalAPIURL(file_path);
    a.download = CommonUtil.getAttachmentLocalAPIURL(file_path);
    a.target = "hiddenIframe"; // if you don't use a iframe, the persistent connections are lost (eg Websocket)
    a.click();
  }

  static downloadImage(url: string, authToken: string, fileName?: string, blob?: boolean): Observable<any> {
    return Observable.create(observer => {
      let req = new XMLHttpRequest();
      req.open("get", url);
      req.responseType = "arraybuffer";
      req.setRequestHeader("Authorization", "Bearer " + authToken);
      req.onreadystatechange = function() {
        if (req.readyState === 4 && req.status === 200) {
          if (fileName) {
            Utils.saveFile(new Blob([req.response], { type: req.getResponseHeader("Content-Type") }), fileName);
            observer.next(fileName);
          } else if (blob) {
            observer.next(new Blob([req.response], { type: req.getResponseHeader("Content-Type") }));
          } else {
            observer.next(window.URL.createObjectURL(new Blob([req.response], { type: req.getResponseHeader("Content-Type") })));
          }
          observer.complete();
        } else if (req.readyState === 4 && req.status !== 200) {
          observer.next(null);
          observer.complete();
        }
      };
      req.send();
    });
  }

  static downloadFile(url: string, authToken: string, name?: string): Observable<any> {
    let fileName = url.replace(/^.*[\\\/]/, "") // this will handle / OR \ in path
            .replace(/\..*/, ""); // this will handle extension
    return Utils.downloadImage(url, authToken, name ? name : fileName);
  }

  static _dlBlobCordova(blob: Blob, filename: string) {
    return Observable.create((obs: Observer<any>) => {
      window["requestFileSystem"](window["PERSISTENT"], blob.size, (fs) => {
        fs.root.getFile(`Download/${filename}`, { create: true, exclusive: false }, (fileEntry) => {
          fileEntry.createWriter((fileWriter) => {
            fileWriter.onwriteend = () => {
              obs.next(null);
              obs.complete();
            };
            fileWriter.onerror = function (e) {
              obs.error(e);
              obs.complete();
            };
            fileWriter.write(blob);
          });
        });
      });
    });
  }

  static getUrlFromBlob(blob: Blob) {
    return window.URL.createObjectURL(blob);
  }

  static flatten(arr: any[]) {
    let values = [];
    if (Array.isArray(arr)) {
      arr.forEach(value => {
        values = values.concat(Utils.flatten(value));
        return;
      });
    } else {
      values.push(arr);
    }
    return values;
  }

  static copyArray(arr: any[]) {
    let newArray = arr.slice();
    newArray.forEach(item => {
      if (typeof item === "object") {
        item = Object.assign({}, item);
      }
    });
    return newArray;
  }

  static extractTextFromHTML(text: string): string {
    let div = document.createElement("div");
    div.innerHTML = text.replace(/(<br\s*[\/]?>|&lt;br \/&gt;)/gi, "\n");
    const val = div.textContent || div.innerText || "";
    div = null;
    return val;
  }

  static getExtensionType(name: string): string {
    if (name.startsWith("image/")) {
      return name.match(/\/(\w+)/)[1];
    }
    let ext: any = name.split(".");
    ext = ext[ ext.length - 1];
    return ext;
  }

  static getDateAfterOffset(days: number) {
    let today = new Date();
    return new Date(today.setDate(today.getDate() + days));
  }

  static getWeekDay(day: number, isShort?: boolean): string {
    let weekdays: string[] = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    if (!weekdays[day]) {
      return null;
    }
    if (isShort) {
      return weekdays[day].substr(0, 3);
    } else {
      return weekdays[day];
    }
  }

  static getFormattedDate(date: Date) {
    if (!date) return null;
    if (!date.getTime) return date;
    if (isNaN(date.getTime())) return null;
    let year = date.getFullYear();
    let month: string = String(date.getMonth() + 1);
    month = `${parseInt(month) < 10 ? "0" : ""}${month}`;
    let monthDate: string = String(date.getDate());
    monthDate = `${parseInt(monthDate) < 10 ? "0" : ""}${monthDate}`;
    return `${year}-${month}-${monthDate}`;
  }

  static getPropertyFromObject(obj: any, prop: string) {
    let value = obj;
    let keys = prop.split(".");
    keys.forEach(key => value = (value || {})[key]);
    return value || "";
  }

  static extractContentPathFromUrl(url: string, baseUri: string) {
    let startIndex = url.indexOf(baseUri) + baseUri.length;
    let paramIndex = url.indexOf("?");
    let endIndex =  paramIndex >= 0 ? paramIndex : url.length;
    return url.substring(startIndex, endIndex);
  }

  static isIOS() {
    return navigator.userAgent.match(/iPad|iPhone|iPod/g) ? true : false;
  }

  static hightlightText(text: string, searchText: string): string {
    let replacement: string = `<span class="search-high-light">${searchText}</span>`;
    text = text.replace(new RegExp(`<span class="search-high-light">`, "ig"), "");
    text = text.replace(new RegExp(`</span>`, "ig"), "");
    return text.replace(new RegExp(searchText, "ig"), replacement);
  }

  static getQueryParamsFromStringUrl(url: string): any {
    let obj = {};
    if (url.includes("?")) {
      url
        .split("?")[1]
        .split("&")
        .forEach(param => Object.assign(obj, {[param.split("=")[0]]: param.split("=")[1]}));
    }
    return obj;
  }

  static compareDatetime(date1: Date, date2: Date, checkTime?: boolean) {
    let _date1 = new Date(date1);
    let _date2 = new Date(date2);
    if (!checkTime) {
      _date1.setHours(0, 0, 0, 0);
      _date2.setHours(0, 0, 0, 0);
    }
    return _date1.getTime() < _date2.getTime();
  }

  static getDateFromString(date: string, local?: boolean) {
    const [time, offset] = date.split("+");
    const [hours, minutes] = offset.split(":");
    let tempDate = new Date(time);

    if (local) {
      const timezoneOffset = new Date().getTimezoneOffset();
      tempDate.setMinutes(tempDate.getMinutes() - timezoneOffset);
    } else {
      tempDate.setHours(tempDate.getHours() + parseInt(hours));
      tempDate.setMinutes(tempDate.getMinutes() + parseInt(minutes));
    }
    return tempDate;
  }

  static escapeRegExpStr(str: string) {
    return str.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
  }

  static insertAt(base: string, insert: string, index: number) {
    return base.slice(0, index) + insert + base.slice(index);
  }

  static isAndroid() {
    return /android/i.test(navigator.userAgent || navigator.vendor) ? true : false;
  }

  static isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|PlayBook/i
      .test(navigator.userAgent);
  }

  static  isToday = (someDate) => {
    someDate = new Date(someDate);
    const today = new Date();
    return someDate.getDate() == today.getDate() &&
      someDate.getMonth() == today.getMonth() &&
      someDate.getFullYear() == today.getFullYear();
  };
  static makeid(length = 5) {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const numbers = "0123456789".split("");
    const charactersLength = characters.length;
    for ( let i = 0; i < length; i++ ) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return numbers.includes(result.substring(0, 1)) ? "_" + result.substring(1, length + 1) : result;
  }
  static convertJsonArrayToJson(sampleArray:any[]):any{
    const obj = {};
    sampleArray.forEach(function(value, idx) {
      obj[(idx) + ""] = value;
    });
    return obj;
  }
  static convertJsonToJsonArray(sampleArray:any):any[]{
    const obj = [];
    Object.keys(sampleArray).forEach(function(idx) {
      obj[idx] = sampleArray[idx];
    });
    return obj;
  }
}

export const DEFAULT_AVATAR = "\ndata:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGwAAABsCAYAAACPZlfNAAAAAXNSR0IArs4c6QAADuxJREFUeAHtXUuMHMUZrup57Dz2vV4wxsYkOFopmEccmxiMcrCUSzCOkotzIYo5GCU3ELk4BxSk+BIUbkhsJBMlF5wDEo/kEsmHCIPBGwPGjmQFC7AxGK/Xu96d1+7OTOf7erZne3bn1TNdVT1hSxp1Tdfrr//rev31119S9JCzbVvm8/mtZcuasEtiwpLlCby6x5ZiWNpiQEgxgDgDUsoBVgv+BfgXhC0WEGcBceaktC+VbeuijIiLVrl8MZlMfoE4dq+wQYaZUDA8BoD22FLut22xH7Q+hHfpIGkGWFnk976U4qS07ZMA8AzeLQdZRpB5hQ4wADKayy0esoV9EK3iUTST/iAr3DIvKTNob29LIV9Ppfr+BvButkyjMUIoAGNLyuWWflyWpV+AUQfwP66RBw2LAlhL+HDesuzIX1Kp+D/C0PKMAgZgxrO5wtNgyhFwbawh58IRMIOPaTKdSrwI4KZNkWQEsFzOvrMs8s8KIY8AtJSpyndSLsDKYTozaYnkC6mUvNpJHt2k0QrYgm3fJgqF32GMeDIs3V6nzHO6SymOi0TiOUxLr3eaj990WgADOBYmEk+VRfkYCBz2S2TI489ZwjqKCcrLALGsmlblgGWzS5iWF1/CtHy36sqYzB/LgilpR3+VTsenVNJhqcocrSqWyRX+YMvS6f93sMhD1hF1fc+pM+quiq9KWhgWu3cVbfsEJAx7VREe6nylOB2V8hAW4ZeDpjPwFpbNFg4Wy/aH31iwiBA+VPKAvAgasEBb2EI2/3tQezRoIuvlhy5X5PKLmHQWRGFxWSwuL4niclGUSpjalMuihF80EhHRaETE4zGRTCREOolfOikwOaiXpaJ38thAOvnboDIPhHIwL4oF8CQWwIeDIqxePsViUczNL4j5hZzI5fKiDND8ukjEEoMD/WLT6JADot/0ncTHgvsVLLiP4EMpdpLem6ZrwABWMpvPnwDvHvdmHKQ/k82L6ZlZsZChnDY419+fEnfcNqYFODTqN9PJ5CGAlu+mBl0BBrCGM/nCW5gi7euGiEZpc/mC+PLaNLq+QqMogbzfNDosNgM4ywp8SK+lT8pT/cnEAYA2VxvQ/r+OAWPLAlj/VAXWl19PixszHderfQ6sxOzDOLd92xaR6FMsd66A9qNOW1pHgDljVj7/mqpu8LMrX2KcCrb7awdBtrBtd94uhjDGqXQr3eNPAVrJbzkd9QHOBEPRmHUDY5UJsMg4zi4/v/KVmLu14JePvuLzQwcP/+Qr0Upk34Bx6q5yNsjpuGl3+eo1MTunGDTMqCvLIH+19QVYZSGodp1VWFzyVwNFsb/46prIZLGTotTZR/0urtsewyBu2o7V+wegf0RVHWZmb4mrX2nbqWhZDa7Z7rl7m+qJyGzUkg+2K8Zqq4VhkhGDbPBVVWCVy7ZgNxQmsIgmpSbXrs+0BLbLCCOUu5LH7eTTFmDZ/OIxlbLBzy5fVT7Qt8OMenHmFzIiX1isFxTcO8geHR63kWNLwLifBfngM23k1VGUm7PzIgMxU5jd9WkdilP2MxVeN+dEU8DQTK3K5qPdNF7zIpqHzmcyzSOEIJQiMfBCKSXt8ropENzWB51Kd4pVi52C4DKFzNwZUO3Ia/K8WTkNAaPCzIoORrP0XYXxqy0WfS/2uyqz08Tqp/gVyshzR1mpAaENAcNG0/NIo1RhZgn7V73iCqonHquMGKZm2erfWl9dwKg3CFW0w7VRg/+3jP2tXnHFkj5aqQZIDOrxpi5gVPJEd6VYbI11To90h2RcEWsyXY68ryjari9xHWCIPE6N3PVRg39TLPXG+MWacxGt1zla0cCi1q0DzNF116Q+Tel4rzjdtKLhpIjFWv7UAIZIsZWDCWvjKflPkVSvuIjq3eg6jCAWxMQbVAMYjvw8hsCwnyLx0q/NT0GwATfGY1jecmuowPmsJ7yBqv3YcVVdRGD5R6AyZ8LxzJy33CpgaHqjPEznDVTtp85gr7iIZYbWlQOOoy6fqoA5x1Q1TOXdgvmk4kuvuFg8aoRUNKQ4sXELrwKGAS5wtWK3kEbPvljvANafSjaqhvL3XmwcwJzZIQ+Aa3bRWFS9LmBAdUqbBAzYuLNFBzCaVsD+gVrdrgaM60+H/8RsH3QVjY63wMbBCDystDDYwWjAT+WvhweNfCe+6tWfMv9R0VYJia4AVjFa4qsSQUUeGEhrPk3in/J0OuE/UcApsBPlAAbjL7bM5gvYggnWwkwrer+enhHXb8yKVKIPR4WK2BfTJw1vRdva8AEcmsjlCiKR7BP3bN+6NljLf6xZszguNWChb9yqGyzW8NZ8xtl2x8cSarBI60Im55w3y+IUTS5vRv+EGBEri4a2SJRuhybek27Z4JYQsbJoFc0E5wzJ5rquqgkhsEu0Y8GOJuzcFzqf8R5aNHv5wuO3phyxsjDn2GGCgFjMjGyum7palhQmPzRiBb1DtYo2jRiU6OtrFBTa90kcajfpiJUFhQ8jK9eUQVFPp0w3vYAmVhbNrnZagW7SUVJvVNzTAfFDWOQbdcAKY5htBDBWfNA0A3xwn2efE1jkm3TEysIK2hhgm0aU6qkGytsxWBow7YiVI0s0RQi/2E1jys4HBlatWDQmRoYGA8uvm4zYJao9zNuCOho2ScGsUJjdljs2Yd/OvP4JsWKXaBQwlC/uvmuLMLmj2+xjuX18TLkZiGble8OIVRQnK40CRoI4W/z23VsdIfCNm3OOFN9LqAl/LBoVWzaPi6Ew7dcBKy6cjQPmAhIFk/rThqfO/ICg0jaxY3u4wAJdxIoLZ332gVxkmjxThqUJJK2vLxZKXRNihTHMvtSEf9qDOLinkuY0lFjhsIrNiJXFi2O0o9KiQO7wmnQ0yxdGR6ws3vITNuIGDTKMs9aBkGpyOTcy8UqmsAFGqbiptRlNzCq3m9ghw4mVtXJ/ln5bdy2INiUKGh40JqlryhG0/Cyx4sKZ2hXvN41tIJDrH93SBZYXqnVXLd9xx5msGExBt32yNsz8P2e2qHnPbGR4SJjU2WjGdRcjR/jLm+maRTYVRp1FXY6L9tvHq6d6dBXbdjkuRg5g6BvPQP02dDaEYhoVdSjLpIQjlA7YOBiBuEoLw52PGMneDhuxOhloUhuqFd+JDcavZcar7ofhpN8brRLqDgeR2orUWJTvOgGb191EVcBw/9UJMCgc9ltXqNMHl17DKS7z23kSE16e6satAoaAmzjp95YbEIZnGXs/utziUqi+1Wq1iQmxcV9UAeML3qbqBoThaWu048HTKdjRDUO1a2hYi0kNYLz6FrFnalIY/LOs0dobLd3wdErI3MwKJlWyagBD01vGADdZDTXsWVp2JkbaqJi9Na+trHYKIhbExBu3BjAGrNxTrNpgu5eGhn7lxpHXlMwza2ExuAmgcsRiDYmr03o3ABFxqbRtvJXxRKZu87I0FXvj5qzLCsNPm60LWNS6dS2MwbxUGpGNTptobduEm8aNSqZvpyDviUG9+tcFjDeAQ+HjeL0EOt4tLi1Dc6o6k9VRZLUMzhR56YFuc3tVAuAh7xvdwt5wbeoYCs7lubmpVUeZdoB58YDpr5x6Jd+CvqSBk6JzUGqZaHT7et0WRrSZgDeA06/DcQnEG4U++fSycbBYXx4+/+TTKwJGJnVUv1oGed4ILEZq2MIYiO7Bwv2W74GZSmzX8+opXj6axY9ghdVo89jIkBjHGQDVAmLIM6dwT+YPMIaVyf96rilgTFC5mp63nbe+HYLdGZlewo/TYxo2pl1fjge2zet6IWgB+lwQM15YptD1GFPvHY3AjGGTk1pdYGq9KB2/I0jSjuxNp+NnmmXSVqmV69rLz3ozIvPzsLGBFuhMvyna6SWjy966+PVTSYenMamwQ8XXRCLe9U61lNYL/anEb1rR0hZgaBWxTD7/r6Wl4t5buG6QC8wcLrzecKsciMMyHbtM54eNVxoUS8Tjzv+WrVGK0/3J5A8Rr0aqsZr7qq8twBj9woVLd83ls7iy3g7/ga7V+oXCR8sDBJA/qj30o2VSJWHF+brwrW3AmPm7Zz86WC6J6maaW+LG0z8HeJgRV97j2PDgTzaPj7S9edxwWl+PhId3PYCMrWP1wjbe+eMA73KZnpk/5gcsluCrhbkkvXvm3HHM+w67/zee/jkA8w2vPLzn/if9puwIMExCou9OnXsNa93H/Ra4Ed9pJW8+vPv+n2GS4dvmoK8u0WW2U9Dm0UNYipxy32082+OAwzOHd/7BYgkdAcaEj2zblk9GBg9sgEZutOfIK/KMvGsvxfpYHXWJ3mzeuXIlKa7dPLHRPXq5st4PRr8p0LK6AYu5dg0YM+GYdnrq48mNiQi5sd5xgrF3931HOhmz1ubWcZfozYiEVGY8G1N+L18qfusYeRMEWMwvkBbmJdJZXJfln7/xEhEpZy3L/mVl7erlUHf+wAEjOafO/me7KBdfBWh7uyOvR1NLeVpY0Z/v2/Xdz4OugRLASOTUlB1bEucgFZHPYIwLpOsNuvJB54duD/tY9h/j4v6ju3e3FuR2Ur4ywFxiTn1wfo8oll9CRZRsgrrlmH/KKRG1fr3vezub7md1S6dywEggW9jpf59/yrnI29arI9Itg1qmlzB2gm39vd/f+XKlhbVM0VUELYC5FJ766JPbZDH3PM44HAaIyq8ddstV8QQ4UAO0j9ux9HP7HthxXUUZ9fLUCphLwDsfXrxTLC8+KyQv5RThtGLiErvmCWlFDnpokyLW98IjD05cXROs/K8RwNxanT373/FCOfc0GHAE2h5j7vswPqHnPoMPbDJhpV7ctes76zRyddFsFDC3kpUZ5cePYbB7AtotB8LSXTrdno0zc1L+NS7u+7uqmZ/Lh3aeoQDMS+g7Fy6Mynz5ELSsDuLOrEcBol4z7TgAjhP7OFNsvWEnrROP3HuvGRVkL1M8/tAB5qGtspaLnN9jl+z9GOD3g9iHMOalvXG69WNMykJwDcMy8qSMyJPx0s4zYWhJjeoVasDWEo2uUp45f35radmeAHDODyfsd4Dhw2A8WiJMutuwEu7a4qe1VUmbxhKa5yKD9zyacglxL/IXicmLe3bu/AJdH7LoDfc/qtmAl6kNqloAAAAASUVORK5CYII=\n";
export const DEFAULT_IMG = `data:image/png;base64,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`;

export const DEFAULT_SOCIAL_AVATAR = "data:image/svg+xml;base64,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";
